import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import { styled } from '@mui/material/styles';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { QuizColorsMode } from '~/blocks/TestCard/TestCard';
import CustomDragLayer from '~/components/Test/OrderingQuestion/CustomDragLayer';
import OrderingQuestionAnswer from '~/components/Test/OrderingQuestion/OrderingQuestionAnswer';
import { QuizQuestionAnswer } from '~/types';
import { toBoolean } from '~/utils/toBoolean';

interface OrderingQuestionProps {
  answerOptions?: (QuizQuestionAnswer | null)[] | null;
  handleOrderUpdate: (options: string[]) => void;
  order: string[];
  isSmall?: boolean;
  disabled?: boolean;
  displayResult?: boolean;
  isCorrectByIdMap?: null | any;
  colorsMode?: QuizColorsMode;
}

const OrderingQuestion: FC<OrderingQuestionProps> = ({
  answerOptions,
  handleOrderUpdate,
  order,
  isSmall = false,
  disabled = false,
  displayResult = false,
  isCorrectByIdMap,
  colorsMode,
}) => {
  const [cards, setCards] = useState<QuizQuestionAnswer[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [interacted, setInteracted] = useState<boolean>(false);

  //copy answerOptions for manipulations
  useEffect(() => {
    if (answerOptions) {
      setCards(answerOptions.filter(toBoolean<QuizQuestionAnswer>));
      //reset interacted for new question
      setInteracted(false);
    }
  }, [answerOptions]);

  //reorder based on order
  useEffect(() => {
    if (order?.length && !interacted) {
      setCards((prevState) => {
        const cardsById = {};
        prevState.forEach((card) => {
          if (card?.id) {
            cardsById[card?.id] = card;
          }
        });
        return order.map((id) => cardsById[id]);
      });
      setInteracted(true);
    }
  }, [order, interacted, cards]);

  const moveCard = useCallback((dragIndex: number, hoverIndex: number) => {
    setCards((prevState) => {
      const cards = [...prevState];
      const dragCard = cards[dragIndex];
      cards.splice(dragIndex, 1);
      cards.splice(hoverIndex, 0, dragCard);
      return cards;
    });
  }, []);

  const handleDrop = useCallback(
    (id, index) => {
      if (!order?.length || order[index] !== id) {
        const cardIds = cards.filter(toBoolean<string>).map((card) => card?.id) as string[];

        //ensure that on start drag and drop to same position will not show as interacted
        if (!order?.length) {
          const defaultCardIds =
            answerOptions?.map((answer) => answer?.id)?.filter(toBoolean<string>) || [];
          if (defaultCardIds.join(',') === cardIds.join(',')) {
            return;
          }
        }
        handleOrderUpdate(cardIds);
        setInteracted(true);
      }
    },
    [cards, answerOptions, handleOrderUpdate, order],
  );

  return (
    <QuestionWrapper ref={containerRef}>
      <DndProvider backend={HTML5Backend}>
        <CustomDragLayer
          width={containerRef?.current?.clientWidth}
          answerOrder={interacted ? order : []}
          isSmall={isSmall}
          colorsMode={colorsMode}
        />
        {cards?.map((card, index) => {
          const correctCardOrder = isCorrectByIdMap?.[card?.id || ''];
          const currentCardOrder = order?.indexOf(card?.id || '');
          const isCorrect = correctCardOrder === currentCardOrder;

          return (
            <OrderingQuestionAnswer
              key={card?.id}
              index={index}
              id={card?.id}
              title={card?.title}
              orderIndex={interacted ? currentCardOrder : null}
              moveCard={moveCard}
              handleDrop={handleDrop}
              isSmall={isSmall}
              disabled={disabled}
              displayResults={displayResult}
              isCorrect={isCorrect}
              colorsMode={colorsMode}
            />
          );
        })}
      </DndProvider>
    </QuestionWrapper>
  );
};

export default OrderingQuestion;

const QuestionWrapper = styled('div')`
  position: relative;
`;
